// Moat Runner — game data
// Another deliberately different shape, same family as Signal Boost and The
// Inner Citadel: no budget math, no resource pools. The skill being trained
// is pattern recognition — spotting which unglamorous businesses are quietly
// protected by a real moat, and where the current AI buildout actually pays,
// before either window closes. Three stages: The Pitch Floor (classify a
// business by its moat) -> The AI Layers (classify a scenario by which layer
// of the AI wave it's in) -> The Venture (high-stakes decisions running one
// of these businesses) -> Empire Report.

// ---------------------------------------------------------------------------
// STAGE 1 — THE PITCH FLOOR: five moat archetypes behind "boring" businesses
// that quietly print money because almost nobody competes for them. Eight
// real pitches, each protected by exactly one of these moats.
// ---------------------------------------------------------------------------
export const MOAT_TYPES = [
  { id: "REGULATORY", label: "Regulatory Moat", icon: "📜" },
  { id: "RECURRING", label: "Recurring Contract", icon: "🔄" },
  { id: "PAID_TWICE", label: "Paid Twice", icon: "💰" },
  { id: "HARD_DOOR", label: "Hard Door", icon: "🚪" },
  { id: "INVISIBLE", label: "Invisible Edge", icon: "🙈" },
];

export const MOAT_MOMENTS = [
  {
    id: "m1",
    text: "A restaurant's insurance policy is void the day their kitchen hood goes even one month past its required professional cleaning — it isn't optional, and it isn't negotiable.",
    correct: "REGULATORY",
    explain: "Fire code and the insurance underwriter set this schedule, not the customer's mood. Demand renews itself by law.",
  },
  {
    id: "m2",
    text: "Every elevator in the city carries a legally mandated annual inspection deadline. No building owner gets to just skip a year because business is slow.",
    correct: "REGULATORY",
    explain: "An inspection deadline set by code doesn't care about the economy — the service renews whether the owner feels like it or not.",
  },
  {
    id: "m3",
    text: "A property manager signs on for valet trash pickup once, and the checks keep arriving every month after with zero additional sales calls.",
    correct: "RECURRING",
    explain: "The sale happened once. The revenue didn't stop — that gap between selling and getting paid is the whole point of a contract.",
  },
  {
    id: "m4",
    text: "Three cell carriers each pay a rooftop owner thousands of dollars a month to keep antennas mounted up there, locked in for 10 to 25 years at a time.",
    correct: "RECURRING",
    explain: "A decade-plus contract with a company that isn't going anywhere is about as close to guaranteed monthly income as real estate gets.",
  },
  {
    id: "m5",
    text: "A route driver gets paid by a restaurant to haul away its used cooking grease — then drives straight to a biofuel refinery and gets paid again for the same tank.",
    correct: "PAID_TWICE",
    explain: "Same job, two payers. Removing the waste is the first payday; selling it as a commodity is the second.",
  },
  {
    id: "m6",
    text: "Getting certified to legally repair commercial elevators takes years most people aren't willing to put in — which is exactly why almost nobody else shows up to bid against you.",
    correct: "HARD_DOOR",
    explain: "The certification is annoying to get and that's the entire point — it's a door that's hard to walk through, so most competitors never do.",
  },
  {
    id: "m7",
    text: "A mobile headstone-cleaning and restoration business has almost no competitors — mostly because almost nobody wants to spend their workday walking through a cemetery.",
    correct: "INVISIBLE",
    explain: "Being psychologically repellent to most people isn't a downside here — it's the reason the field stays empty.",
  },
  {
    id: "m8",
    text: "Porta-potty rental companies rarely lose sleep over a hot new competitor entering the market, since basically nobody grows up dreaming of running a portable-toilet fleet.",
    correct: "INVISIBLE",
    explain: "Nobody's fighting you for a business that sounds unglamorous out loud — that's free real estate, competitively speaking.",
  },
];

// ---------------------------------------------------------------------------
// STAGE 2 — THE AI LAYERS: three ways to actually engage with the current AI
// buildout, from the sources' "Fifth Industrial Revolution" framing. Six
// scenarios, each landing in exactly one layer, two per layer.
// ---------------------------------------------------------------------------
export const AI_LAYERS = [
  { id: "LAYER1", label: "Layer 1", subtitle: "Infrastructure — the shovel sellers" },
  { id: "LAYER2", label: "Layer 2", subtitle: "Business Implementation" },
  { id: "LAYER3", label: "Layer 3", subtitle: "Career Shielding" },
];

export const LAYER_SCENARIOS = [
  {
    id: "l1",
    text: "A manufacturer builds the specialized cooling systems that keep massive AI data centers from overheating under nonstop workloads.",
    correct: "LAYER1",
    explain: "A shovel seller gets paid regardless of which AI company's software eventually wins the market — the infrastructure gets used either way.",
  },
  {
    id: "l2",
    text: "An investor buys shares in a semiconductor company that supplies chips to nearly every major AI lab at once.",
    correct: "LAYER1",
    explain: "Same logic as the picks and shovels of a gold rush — you don't have to bet on the miner to profit from the mining.",
  },
  {
    id: "l3",
    text: "A consultant walks into a decades-old plumbing company still scheduling every job on paper, and sets up simple automation for dispatch and invoicing.",
    correct: "LAYER2",
    explain: "An estimated 44% of American businesses haven't started using AI at all yet — that gap is the entire opportunity in this layer.",
  },
  {
    id: "l4",
    text: "A small bakery swaps a $2,000-a-month marketing contractor for an AI writing and scheduling tool that costs $200, and reinvests the difference.",
    correct: "LAYER2",
    explain: "Using AI to gain a structural cost advantage over competitors is implementation, even when nobody's hired as a consultant to do it.",
  },
  {
    id: "l5",
    text: "An accountant starts using AI-assisted audit tools and begins closing client books in half the time it takes her peers.",
    correct: "LAYER3",
    explain: "Learning the tools directly is estimated to boost productivity by up to 40% — this is the individual version of the same wave.",
  },
  {
    id: "l6",
    text: "A capable employee ignores every new AI tool rolled out at the office, while a slower-but-adapting coworker quietly starts producing more in the same hours.",
    correct: "LAYER3",
    explain: "AI probably isn't replacing most jobs soon — but workers who use it are already replacing the coworkers who won't.",
  },
];

// ---------------------------------------------------------------------------
// STAGE 3 — THE VENTURE: six high-stakes moments running one of these
// businesses, where the "boring but protected" choice competes against the
// "flashy but exposed" one. Each has one clean answer, one merely survivable
// one, and one that quietly gives up the moat.
// ---------------------------------------------------------------------------
export const VENTURE_ENCOUNTERS = [
  {
    id: "v1",
    title: "The Trendy Detour",
    text: "Your grease-trap pumping route quietly nets about $4,000 a month. A friend wants you to drop it and pour everything into a flashy energy-drink startup that's all over everyone's feed this week.",
    choices: [
      {
        id: "keep-boring",
        label: "Keep the grease-trap route running and fund a small side experiment with the profits it's already producing",
        tag: "good",
        result: "The boring route paid for the experiment without ever putting the reliable income at risk.",
      },
      {
        id: "hedge-sale",
        label: "Sell the grease-trap accounts at a discount to free up time, just in case the trendy idea takes off",
        tag: "weak",
        result: "You gave up a proven, paid-twice income stream to chase something that hadn't proven anything yet.",
      },
      {
        id: "chase-trend",
        label: "Shut the route down and put the whole cash reserve into the trendy startup because everyone's talking about it",
        tag: "bad",
        result: "Attention isn't a moat — six months later the feed had moved on, and so had the reserve.",
      },
    ],
  },
  {
    id: "v2",
    title: "The First Contract",
    text: "You're about to land your first HOA valet-trash contract, and the property manager offers a friendly year-to-year handshake instead of putting anything in writing.",
    choices: [
      {
        id: "written-contract",
        label: "Push for a written multi-year agreement with an auto-renew clause before doing a single day of work",
        tag: "good",
        result: "The entire value of this business is getting paid without re-selling constantly — a handshake can evaporate the moment the property manager changes jobs.",
      },
      {
        id: "accept-handshake",
        label: "Accept the handshake deal, and make a mental note to bring up a real contract again next year",
        tag: "weak",
        result: "It worked out this time, but the income was one phone call away from vanishing the whole time.",
      },
      {
        id: "skip-it",
        label: "Skip the contract conversation entirely since the property manager \"seems trustworthy\"",
        tag: "bad",
        result: "Trustworthy people leave jobs, get replaced, and forget verbal promises — none of that is a moat.",
      },
    ],
  },
  {
    id: "v3",
    title: "The License Shortcut",
    text: "A friend offers to let you shadow — and quietly work under — his commercial elevator inspection license instead of going through certification yourself.",
    choices: [
      {
        id: "get-certified",
        label: "Decline, and go through the real certification process even though it's slower",
        tag: "good",
        result: "The license itself is the hard door keeping competitors out later — skipping it means you never actually own the moat.",
      },
      {
        id: "shadow-first",
        label: "Take the shortcut for a few jobs first, to see if you even like the work before committing to certification",
        tag: "weak",
        result: "You got a taste of the work, but every job you did this way was built on someone else's license, not yours.",
      },
      {
        id: "stay-under",
        label: "Build the whole operation on working under someone else's license indefinitely, since certification costs money",
        tag: "bad",
        result: "The \"business\" was never actually yours — one falling-out with your friend and the whole thing disappears.",
      },
    ],
  },
  {
    id: "v4",
    title: "Two Buyers, One Load",
    text: "You've been quietly pumping grease traps for months. A biofuel company just found out and wants to talk about buying your waste oil.",
    choices: [
      {
        id: "standing-agreement",
        label: "Negotiate a standing purchase agreement so every future load already has a buyer lined up",
        tag: "good",
        result: "One job now automatically pays twice, every time, without you having to re-sell it each week.",
      },
      {
        id: "one-off-sale",
        label: "Sell them this one load, but don't bother setting up anything ongoing",
        tag: "weak",
        result: "You got paid twice once — then went right back to only getting paid once.",
      },
      {
        id: "dump-it",
        label: "Just dump the waste oil like before, since dealing with a second buyer sounds like extra paperwork",
        tag: "bad",
        result: "You were sitting on a second revenue stream from a job you were already doing, and let it go to a landfill.",
      },
    ],
  },
  {
    id: "v5",
    title: "Picked For The Wrong Reason",
    text: "You're choosing your first venture: a mobile car-detailing app you'd enjoy describing at parties, or a document-shredding route built around HIPAA and FACTA compliance that makes people's eyes glaze over.",
    choices: [
      {
        id: "pick-shredding",
        label: "Pick the shredding route — the compliance mandate is exactly what makes the demand reliable, and \"boring\" is what's kept competitors away",
        tag: "good",
        result: "Nobody else wanted to compete for this, which is the whole reason the margins stayed high.",
      },
      {
        id: "pick-detailing-later",
        label: "Pick the car-detailing app because it's easier to explain, planning to \"revisit the boring idea later\"",
        tag: "weak",
        result: "Later kept slipping — an easier story to tell isn't the same thing as a better business to run.",
      },
      {
        id: "pick-detailing-impressed",
        label: "Pick the car-detailing app and assume sounding impressive at a party is the same as being a good business",
        tag: "bad",
        result: "It was fun to talk about and hard to keep customers for — competitors who also wanted to sound cool showed up fast.",
      },
    ],
  },
  {
    id: "v6",
    title: "The Clock On The Window",
    text: "It's 2026, and small businesses in your area are still mostly not using AI at all. You keep meaning to start offering implementation help, \"once things settle down.\"",
    choices: [
      {
        id: "start-pilot",
        label: "Start now with one small pilot client, since the early-mover window is generally expected to close somewhere around 2027–2030",
        tag: "good",
        result: "A rough start this year beats a polished plan for a window that's already closing.",
      },
      {
        id: "keep-researching",
        label: "Spend another six months only researching before ever approaching a single client",
        tag: "weak",
        result: "The research was solid. So was every competitor who started while you were still reading.",
      },
      {
        id: "wait-forever",
        label: "Decide to wait until \"AI settles down\" before starting at all",
        tag: "bad",
        result: "The window was never going to hold still and wait for a calmer moment — it just closed a little more every quarter you waited.",
      },
    ],
  },
];
